import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Company } from '../types';
import companyService from '../services/companyService';
import { debounce } from 'lodash';
import '../styles/CompanyAutocomplete.css';

interface CompanyAutocompleteProps {
  value?: string;
  companyId?: string;
  onChange: (company: Company | null, companyName: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({
  value = '',
  companyId,
  onChange,
  onBlur,
  placeholder = '会社名を入力',
  disabled = false,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<Company[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // クリック外の処理
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 検索処理（デバウンス付き）
  const searchCompanies = useCallback(
    debounce(async (searchTerm: string) => {
      if (searchTerm.length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await companyService.searchCompanies(searchTerm);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        console.error('Failed to search companies:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);

    // 値が変更されたら親コンポーネントに通知（会社IDはクリア）
    onChange(null, newValue);

    // 検索実行
    searchCompanies(newValue);
  };

  const handleSelectCompany = (company: Company) => {
    setInputValue(company.name);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // 親コンポーネントに選択した会社を通知
    onChange(company, company.name);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectCompany(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleInputFocus = () => {
    if (inputValue && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // 少し遅延させて、サジェスションのクリックイベントが発火するようにする
    setTimeout(() => {
      setShowSuggestions(false);
      if (onBlur) onBlur();
    }, 200);
  };

  return (
    <div className="company-autocomplete" ref={wrapperRef}>
      <div className="input-wrapper">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="company-input"
          autoComplete="off"
        />
        {isLoading && <span className="loading-indicator">🔍</span>}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {suggestions.map((company, index) => (
            <div
              key={company.id}
              className={`suggestion-item ${
                index === selectedIndex ? 'selected' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // blurイベントを防ぐ
                handleSelectCompany(company);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="company-name">{company.name}</div>
              {company.reading && (
                <div className="company-reading">({company.reading})</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyAutocomplete;